import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

export function useSystemStatus(enabled: boolean) {
  const [snapshot, setSnapshot] = useState<{ data: any; error: string | null; receivedAt: number | null }>({ data: null, error: null, receivedAt: null });
  const active = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);
  const pending = useRef<Promise<void> | null>(null);

  const refetch = useCallback((): Promise<void> => {
    if (!active.current) return Promise.resolve();
    if (pending.current) return pending.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    const task = (async () => {
      try {
        const data = await api.getSystemStatus(controller.signal);
        if (active.current && controllerRef.current === controller) setSnapshot({ data, error: null, receivedAt: Date.now() });
      } catch (error) {
        if (active.current && controllerRef.current === controller) setSnapshot(previous => ({ ...previous,
          error: controller.signal.aborted ? 'Сервер не ответил за 8 секунд' : (error as Error).message,
        }));
      } finally {
        window.clearTimeout(timeout);
        if (controllerRef.current === controller) { controllerRef.current = null; pending.current = null; }
      }
    })();
    pending.current = task;
    return task;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    active.current = true;
    setSnapshot({ data: null, error: null, receivedAt: null });
    let cancelled = false;
    let timer: number | undefined;
    async function tick() {
      await refetch();
      if (!cancelled) timer = window.setTimeout(tick, 2000);
    }
    void tick();
    return () => {
      cancelled = true;
      active.current = false;
      window.clearTimeout(timer);
      controllerRef.current?.abort();
      controllerRef.current = null;
      pending.current = null;
    };
  }, [enabled, refetch]);

  return { ...snapshot, refetch };
}
